import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Custom hook to get facilityId that handles multi-tab scenarios
 * 
 * KEY FEATURES:
 * 1. Always prioritizes Redux state (tab-specific) over localStorage (shared)
 * 2. Each tab maintains its own facilityId independently
 * 3. Changes in one tab don't automatically affect other tabs
 * 
 * This solves the issue where opening 2 tabs in the same browser causes
 * facilityId conflicts because localStorage is shared across tabs.
 * 
 * @returns {Object} - { facilityId, activeFacilities, isLoading }
 */
const useFacilityId = () => {
  const activeFacilities = useSelector((state) => state.activeFacilities.facilities);

  // Get facilityId from Redux state (first facility if multiple selected)
  // Redux state is tab-specific, so each tab has its own activeFacilities
  // 
  // IMPORTANT: This ensures complete tab isolation:
  // - Tab 1 selects Facility A → Redux Tab 1 = [A], localStorage = "A"
  // - Tab 2 selects Facility B → Redux Tab 2 = [B], localStorage = "B"  
  // - Tab 1 changes to Facility C → Redux Tab 1 = [C], localStorage = "C"
  // - Tab 2 reads facilityId → Returns "B" from Redux Tab 2 (NOT "C" from localStorage)
  // 
  // localStorage changes from other tabs are IGNORED once Redux state exists.
  const facilityId = useMemo(() => {
    // ALWAYS read from Redux first (tab-specific, isolated per tab)
    if (activeFacilities && activeFacilities.length > 0) {
      return activeFacilities[0];
    }
    
    // Fallback to sessionStorage ONLY if Redux state is empty (initial page load)
    // Once Redux has data, sessionStorage changes from other tabs won't affect this tab
    try {
      return sessionStorage.getItem("facilityId");
    } catch (error) {
      console.warn('Error reading facilityId from sessionStorage:', error);
      return null;
    }
  }, [activeFacilities]);

  return {
    facilityId,
    isLoading: false,
    activeFacilities // Also return full array for components that need multiple facilities
  };
};

export default useFacilityId;
